import { AdReaction } from '../advertisement/adReaction';
import { ProductItem } from '../product/productItem';
import { CustomerHealthSettings } from './customerHealth';
import { z } from 'zod';

export type SavedRecipe = z.infer<typeof SavedRecipe>;
export type CustomerLegal = z.infer<typeof CustomerLegal>;
export type CustomerFlags = z.infer<typeof CustomerFlags>;
export type CustomerSettings = z.infer<typeof CustomerSettings>;
export type CustomerTutorialSettings = z.infer<typeof CustomerTutorialSettings>;
export type CustomerSaveSearch = z.infer<typeof CustomerSaveSearch>;
export type UserDetails = z.infer<typeof UserDetails>;
export type CustomerInfo = z.infer<typeof CustomerInfo>;

export const SavedRecipe = z.object({
    recipeId: z.string(),
    deletedOn: z.string().nullish(),
});

export const NOTIFY_CUSTOMER_DONT_STEAL = 'notify_customer_dont_steal' as const;

export const CustomerLegal = z.object({
    legalGuid: z.string(),
    type: z.enum([NOTIFY_CUSTOMER_DONT_STEAL]).or(z.string()),
    content: z.string().optional(),
    validOn: z.string(),
    accepted: z.boolean(),
    acceptedOn: z.string(),
    createdOn: z.string(),
    modifiedOn: z.string(),
});

export const CustomerFlags = z.object({
    enableVrPayment: z.boolean().nullish(),
    enableRecipes: z.boolean().nullish(),
    developer: z.boolean().nullish(),
    enablePosPromotions: z.boolean().nullish(),
});

export const CustomerSettings = z.object({
    enableShoppingListGroupings: z.string().nullish(),
    disableAds: z.string().nullish(),
});

export const CustomerTutorialSettings = z.object({
    easyDeals: z
        .object({
            lastClosed: z.string().nullish(),
        })
        .nullish(),
});

export const CustomerSaveSearch = z.object({
    createdOn: z.string().nullish(),
    itemId: z.string().nullish(),
    modifiedOn: z.string().nullish(),
});

export const UserDetails = z
    .object({
        userGuid: z.string().nullish(),
        hash: z.string().nullish(),

        performedAdvertisements: z.array(z.unknown()).nullish(),
        legal: z.array(CustomerLegal).nullish(),
        availablePaymentMethods: z.array(z.string()).nullish(),
        riskWorkflow: z.unknown().nullish(),
        isCustomerVerifiedBasket: z.unknown().nullish(),
        totalSessionCount: z.number().nullish(),
        flags: CustomerFlags.nullish(),
        raiseMultiSessionTicket: z.boolean().nullish(),
        customerNumber: z.number().nullish(),
        campaigns: z.array(z.unknown()).nullish(),
        settings: CustomerSettings.nullish(),
        openSessions: z.array(z.unknown()).nullish(),
        tutorialSettings: CustomerTutorialSettings.nullish(),
        savedSearches: z.array(CustomerSaveSearch).nullish(),
        surveyAnswers: z.array(z.unknown()).nullish(),
        labels: z.array(z.unknown()).nullish(),
        healthSettings: CustomerHealthSettings.nullish(),
    })
    .passthrough();

export const CustomerInfo = z
    .object({
        userDetails: UserDetails.nullish(),
        advReactions: z.array(AdReaction).nullish(),
        shoppingListItems: z.array(ProductItem).nullish(),
        salesScore: z.unknown().nullish(),
        customerSalesScore: z.array(z.unknown()).nullish(),
        savedRecipes: z.array(SavedRecipe).nullish(),
        redeemableCoupons: z.array(z.unknown()).nullish(),
        collectedCoupons: z.array(z.unknown()).nullish(),
    })
    .passthrough();
